import { useEffect, useRef, useState } from "react";
import { InputNumber, Modal, Typography } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { SketchPicker } from "react-color";
import type { ChartCustomRefLine } from "../../Processing/types";

const { Text } = Typography;

export const REF_LINE_PRESET_COLORS = [
  { color: "#d9534f", title: "Red" },
  { color: "#EF7617", title: "Orange" },
  { color: "#FFC83D", title: "Yellow" },
  { color: "#31B452", title: "Green" },
  { color: "#0000FF", title: "Blue" },
  { color: "#000000", title: "Black" },
  { color: "#cccccc", title: "Grey" },
  { color: "#A9A9A9", title: "Dark Grey" },
];

export interface RefLineModalProps {
  open: boolean;
  /** Line under edit; null adds a new line. */
  initial: ChartCustomRefLine | null;
  defaultColor: string;
  onSave: (line: ChartCustomRefLine) => void;
  onCancel: () => void;
}

export function RefLineModal({ open, initial, defaultColor, onSave, onCancel }: RefLineModalProps) {
  const [value, setValue] = useState<number | null>(null);
  const [label, setLabel] = useState("");
  const [color, setColor] = useState(defaultColor);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const swatchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setValue(initial?.value ?? null);
    setLabel(initial?.label ?? "");
    setColor(initial?.colorHex ?? defaultColor);
    setShowColorPicker(false);
  }, [open, initial, defaultColor]);

  const handleSave = () => {
    if (value == null) return;
    onSave({ value, label: label.trim() || undefined, colorHex: color });
  };

  return (
    <Modal
      open={open}
      title={initial !== null ? "Edit Reference Line" : "Add Reference Line"}
      onOk={handleSave}
      onCancel={onCancel}
      okText={initial !== null ? "Save" : "Add"}
      okButtonProps={{ disabled: value == null }}
      destroyOnClose
      width={360}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
        <div>
          <Text strong style={{ fontSize: 12 }}>Value</Text>
          <InputNumber
            autoFocus
            style={{ width: "100%", marginTop: 4 }}
            placeholder="e.g. 350.5"
            value={value}
            onChange={(v) => setValue(v)}
            onPressEnter={handleSave}
          />
        </div>
        <div>
          <Text strong style={{ fontSize: 12 }}>Label <Text type="secondary">(optional)</Text></Text>
          <input
            style={{
              width: "100%", marginTop: 4, padding: "4px 8px",
              border: "1px solid #d9d9d9", borderRadius: 6, fontSize: 13,
            }}
            placeholder="e.g. Target"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
          />
        </div>
        <div>
          <Text strong style={{ fontSize: 12 }}>Color</Text>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, position: "relative" }}>
            <div
              ref={swatchRef}
              onClick={() => setShowColorPicker(!showColorPicker)}
              style={{
                width: 28, height: 28, borderRadius: 6,
                border: "1px solid #d9d9d9",
                background: color,
                cursor: "pointer",
              }}
            />
            <Text style={{ fontSize: 12, color: "#555" }}>{color.toUpperCase()}</Text>
            {showColorPicker && (
              <div
                style={{
                  position: "fixed",
                  zIndex: 1100,
                  ...((() => {
                    const r = swatchRef.current?.getBoundingClientRect();
                    if (!r) return { top: 0, left: 0 };
                    const pickerH = 310;
                    const fitsBelow = r.bottom + 8 + pickerH < window.innerHeight;
                    return fitsBelow
                      ? { top: r.bottom + 8, left: r.left }
                      : { top: r.top - pickerH - 8, left: r.left };
                  })()),
                }}
              >
                <div
                  style={{ position: "fixed", inset: 0, zIndex: -1 }}
                  onClick={() => setShowColorPicker(false)}
                />
                <SketchPicker
                  color={color}
                  disableAlpha
                  presetColors={REF_LINE_PRESET_COLORS}
                  onChange={(c: { hex: string }) => setColor(c.hex)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export interface RefLineListProps {
  lines: ChartCustomRefLine[];
  defaultColor: string;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
}

export function RefLineList({ lines, defaultColor, onEdit, onRemove }: RefLineListProps) {
  if (lines.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 4 }}>
      {lines.map((rl, i) => (
        <div
          key={i}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "3px 8px", background: "#fff", border: "1px solid #e8ecf0",
            borderRadius: 6, fontSize: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                background: rl.colorHex ?? defaultColor,
                border: "1px solid rgba(0,0,0,0.15)",
              }}
            />
            <span style={{ color: "#333", fontWeight: 500 }}>
              {rl.value}
              {rl.label && (
                <span style={{ color: "#888", fontWeight: 400, marginLeft: 6 }}>
                  {rl.label}
                </span>
              )}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <EditOutlined
              style={{ color: "#999", cursor: "pointer", fontSize: 12 }}
              onClick={() => onEdit(i)}
            />
            <DeleteOutlined
              style={{ color: "#999", cursor: "pointer", fontSize: 12 }}
              onClick={() => onRemove(i)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
